/**
 * Pending Invitations Banner
 *
 * Displays a notification banner when the user has pending space invitations.
 * Shows at the top of the dashboard to alert users of invitations waiting for their response.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import * as invitationsService from '../../services/invitations.service';
import type { SpaceInvitation } from '../../types/firestore';

export const PendingInvitationsBanner = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState<SpaceInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchPendingInvitations = async () => {
      if (!isAuthenticated || !user?.email) {
        setLoading(false);
        return;
      }

      // Check if banner was dismissed in this session
      const dismissedKey = `invitations-banner-dismissed-${user.email}`;
      const wasDismissed = sessionStorage.getItem(dismissedKey) === 'true';

      if (wasDismissed) {
        setDismissed(true);
        setLoading(false);
        return;
      }

      try {
        const pending = await invitationsService.getPendingInvitations();
        setInvitations(pending);
      } catch (error) {
        console.error('Failed to fetch pending invitations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPendingInvitations();
  }, [isAuthenticated, user?.email]);

  const handleDismiss = () => {
    if (user?.email) {
      const dismissedKey = `invitations-banner-dismissed-${user.email}`;
      sessionStorage.setItem(dismissedKey, 'true');
      setDismissed(true);
    }
  };

  const handleViewInvitations = () => {
    // Navigate to the first pending invitation
    if (invitations.length > 0 && invitations[0].inviteToken) {
      navigate(`/invite/${invitations[0].inviteToken}`);
    }
  };

  // Don't show banner if loading, dismissed, or no invitations
  if (loading || dismissed || invitations.length === 0) {
    return null;
  }

  return (
    <div className="bg-sky-blue/10 dark:bg-sky-blue/20 border-b border-sky-blue/30 dark:border-sky-blue/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-sky-blue/20 dark:bg-sky-blue/30 rounded-full flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-sky-blue dark:text-sky-blue"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>

            {/* Message */}
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                You have {invitations.length} pending invitation{invitations.length !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                {invitations.length === 1
                  ? `${invitations[0].invitedByName} invited you to join ${invitations[0].spaceName}`
                  : `Review and accept your space invitations`}
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleViewInvitations}
                className="px-4 py-2 bg-sky-blue text-white text-sm font-medium rounded-lg hover:bg-sky-blue/90 transition-colors"
              >
                {invitations.length === 1 ? 'View Invitation' : 'View Invitations'}
              </button>

              <button
                onClick={handleDismiss}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label="Dismiss"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
